import { APP_ROUTES } from "@/constants/app-routes";
import { QueryKeys } from "@/constants/query-keys";
import {
  trackOnboardingV5Started,
  trackOnboardingV5StepSubmitted,
  trackOnboardingV5StepViewed,
} from "@/features/onboarding/analytics";
import { OnboardingInput } from "@/features/onboarding/components/onboarding-input";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingStepShellV5 } from "@/features/onboarding/components/onboarding-step-shell-v5";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useUpdateUser } from "@/hooks/requests/user/use-update-user";
import { useToast } from "@/hooks/ui/use-toast";
import { triggerHaptic } from "@/utils/haptics";
import { onboardingFadeTransition } from "@/utils/transitions/onboarding-fade-transition";
import { useAuth } from "@clerk/clerk-react";
import { useIonRouter, useIonViewDidEnter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

/** Figma frame 972:1559 fills 2 of the 9 segments on this screen. */
const PROGRESS_FILLED = 2;

const OnboardingNamePage = () => {
  const navigation = useIonRouter();
  const { userId } = useAuth();
  const { user } = useGetUser();
  const queryClient = useQueryClient();
  const { updateUserAsync, isUpdating } = useUpdateUser();
  const { showError } = useToast();
  const [firstName, setFirstName] = useState("");
  const personalInfo = user?.personalInfo;
  const hydratedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useIonViewDidEnter(() => {
    inputRef.current?.focus();
  });

  // While /get-started is disabled this is the first onboarding screen, so reaching it
  // starts the attempt. The view is re-sent because the route tracker in App can run
  // before this effect and drop it for lack of a started attempt; both calls ignore repeats.
  useEffect(() => {
    trackOnboardingV5Started({
      userId: user?.id ?? userId,
      isOnboardingCompleted: user?.isOnboardingCompleted,
    });
    trackOnboardingV5StepViewed(APP_ROUTES.ONBOARDING_FIRST_STEP);
  }, [user?.id, userId, user?.isOnboardingCompleted]);

  useEffect(() => {
    if (hydratedRef.current) return;
    if (personalInfo?.firstName) {
      setFirstName(personalInfo.firstName);
      hydratedRef.current = true;
    }
  }, [personalInfo?.firstName]);

  const trimmedName = firstName.trim();
  const canSubmit = trimmedName.length > 0 && !isUpdating;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    triggerHaptic();
    try {
      await updateUserAsync({
        firstName: trimmedName,
        lastName: personalInfo?.lastName ?? "",
        dob: personalInfo?.dob ?? "",
        sex: personalInfo?.sex ?? "",
      });
      queryClient.setQueryData([QueryKeys.USER], (old: object) => ({
        ...old,
        personalInfo: { ...personalInfo, firstName: trimmedName },
      }));
      trackOnboardingV5StepSubmitted(APP_ROUTES.ONBOARDING_FIRST_STEP);
      // Not navigateToNextStep(): the flow leaves the step router here for the
      // medical question, then rejoins it at the goal step.
      navigation.push(
        APP_ROUTES.ONBOARDING_DIAGNOSTIC,
        "forward",
        "push",
        undefined,
        onboardingFadeTransition,
      );
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <OnboardingStepShellV5
      title="What should I call you?"
      subtitle="Just your first name is fine."
      previousPath={APP_ROUTES.ONBOARDING_START}
      progressFilled={PROGRESS_FILLED}
      footer={<OnboardingPillButton text="Continue" onClick={handleSubmit} disabled={!canSubmit} />}
    >
      <form
        className="w-full"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <OnboardingInput
          ref={inputRef}
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Your first name"
          autoCapitalize="words"
          autoComplete="given-name"
          enterKeyHint="done"
        />
        <button type="submit" className="sr-only" tabIndex={-1}>
          Submit
        </button>
      </form>
    </OnboardingStepShellV5>
  );
};

export default OnboardingNamePage;
